import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ROUTES } from "@/routes";
import { useState, useEffect } from "react";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ProviderSidebar } from "@/components/layout/ProviderSidebar";
import { ProviderHeader } from "@/components/layout/ProviderHeader";
import { SessionDocumentationForm } from "@/components/telehealth/SessionDocumentationForm";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

interface LocationState {
  soapNotes?: {
    chief_complaint: string;
    subjective: string;
    objective: string;
    assessment: string;
    plan: string;
  };
  patientName?: string;
  duration?: number;
}

interface ExistingNotes {
  chief_complaint: string;
  subjective: string;
  objective: string;
  assessment: string;
  plan: string;
  cpt_codes?: string[];
  icd_codes?: string[];
  follow_up_date?: string;
  follow_up_notes?: string;
}

export default function SessionDocumentation() {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const { user } = useAuth();
  const location = useLocation();
  const locationState = location.state as LocationState | null;

  const [loading, setLoading] = useState(!locationState);
  const [sessionData, setSessionData] = useState({
    patientName: locationState?.patientName || "",
    sessionDate: new Date(),
    duration: locationState?.duration || 0,
  });
  const [existingNotes, setExistingNotes] = useState<ExistingNotes | undefined>(
    locationState?.soapNotes ? { ...locationState.soapNotes } : undefined
  );

  const [zoomSessionNotes, setZoomSessionNotes] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId || locationState) return;

    const fetchSessionData = async () => {
      setLoading(true);
      try {
        const [sessionRes, notesRes] = await Promise.all([
          supabase
            .from("telehealth_sessions")
            .select("*, member:profiles!telehealth_sessions_member_id_profiles_fkey(display_name)")
            .eq("id", sessionId)
            .maybeSingle(),
          supabase
            .from("session_notes")
            .select("*")
            .eq("session_id", sessionId)
            .order("updated_at", { ascending: false })
            .limit(1),
        ]);

        if (sessionRes.data) {
          const s = sessionRes.data;
          setSessionData({
            patientName: (s.member as any)?.display_name || "Unknown Patient",
            sessionDate: new Date(s.scheduled_date),
            duration: s.actual_duration_minutes || s.duration_minutes || 0,
          });
          // Set zoom session notes if available
          if ((s as any).zoom_session_notes) {
            setZoomSessionNotes((s as any).zoom_session_notes);
          }
        }

        const latestNote = notesRes.data?.[0];
        if (latestNote) {
          setExistingNotes({
            chief_complaint: latestNote.chief_complaint || "",
            subjective: latestNote.subjective || "",
            objective: latestNote.objective || "",
            assessment: latestNote.assessment || "",
            plan: latestNote.plan || "",
            cpt_codes: latestNote.cpt_codes || [],
            icd_codes: latestNote.icd_codes || [],
            follow_up_date: latestNote.follow_up_date || undefined,
            follow_up_notes: latestNote.follow_up_notes || "",
          });
        }
      } catch (error) {
        console.error("Error fetching session data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSessionData();
  }, [sessionId, locationState]);

  const handleSave = async (data: any) => {
    if (!user || !sessionId) return;

    try {
      const { error } = await supabase.from("session_notes").upsert(
        {
          session_id: sessionId,
          provider_id: user.id,
          chief_complaint: data.chief_complaint,
          subjective: data.subjective,
          objective: data.objective,
          assessment: data.assessment,
          plan: data.plan,
          cpt_codes: data.cpt_codes,
          icd_codes: data.icd_codes,
          follow_up_date: data.follow_up_date?.toISOString().split('T')[0],
          follow_up_notes: data.follow_up_notes,
        },
        { onConflict: "session_id" }
      );

      if (error) throw error;
    } catch (error: any) {
      console.error("Error saving notes:", error);
      toast.error("Failed to save notes");
      throw error;
    }
  };

  const handleSubmit = async (data: any) => {
    if (!user || !sessionId) return;

    try {
      // Save the notes
      const { error: notesError } = await supabase.from("session_notes").upsert(
        {
          session_id: sessionId,
          provider_id: user.id,
          chief_complaint: data.chief_complaint,
          subjective: data.subjective,
          objective: data.objective,
          assessment: data.assessment,
          plan: data.plan,
          cpt_codes: data.cpt_codes,
          icd_codes: data.icd_codes,
          follow_up_date: data.follow_up_date?.toISOString().split('T')[0],
          follow_up_notes: data.follow_up_notes,
        },
        { onConflict: "session_id" }
      );

      if (notesError) throw notesError;

      // Update session status to documented
      const { error: sessionError } = await supabase
        .from("telehealth_sessions")
        .update({ 
          status: "completed",
          cpt_code: data.cpt_codes[0],
        })
        .eq("id", sessionId);

      if (sessionError) throw sessionError;

      navigate(ROUTES.PROVIDER_TELEHEALTH);
    } catch (error: any) {
      console.error("Error submitting notes:", error);
      toast.error("Failed to submit notes");
      throw error;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <ProviderSidebar />
      <div className="ml-64">
        <ProviderHeader />
        <main className="p-6">
          {/* Page Header */}
          <div className="mb-6 flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div className="h-6 w-px bg-border" />
            <div>
              <h1 className="font-display text-2xl font-bold text-foreground">
                Session Documentation
              </h1>
              <p className="text-sm text-muted-foreground">
                Complete post-session notes and billing codes
              </p>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              {zoomSessionNotes && (
                <div className="mb-6 rounded-lg border border-border bg-muted/30 p-4">
                  <h3 className="font-semibold text-foreground mb-2">Zoom Meeting Summary</h3>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">{zoomSessionNotes}</p>
                </div>
              )}
              <SessionDocumentationForm
                sessionId={sessionId}
                patientName={sessionData.patientName}
                sessionDate={sessionData.sessionDate}
                duration={sessionData.duration}
                onSave={handleSave}
                onSubmit={handleSubmit}
                initialSoapNotes={existingNotes}
              />
            </>
          )}
        </main>
      </div>
    </div>
  );
}